import {makeAutoObservable, reaction} from "mobx";
import {DateObservable} from "../../../observables/dateObservable";
import {addHours, isToday, setMinutes} from "date-fns";
import {utcToZonedTime} from "date-fns-tz";

export class EventStartAndEndDetails {
  startDate: DateObservable;
  endDate: DateObservable;
  useValidation: boolean;
  templateDurationTime: number;
  timeZone: string;
  originalStartDate?: Date;
  originalEndDate?: Date;

  constructor(
    timeZone: string,
    startDate: DateObservable,
    endDate: DateObservable,
    useValidation?: boolean,
    templateDurationHour?: number,
    templateDurationMin?: number
  ) {
    makeAutoObservable(this);
    this.startDate = startDate;
    this.endDate = endDate;
    this.useValidation = useValidation ?? false;
    this.templateDurationTime = this.getDurationTime(
      templateDurationHour,
      templateDurationMin
    );
    this.timeZone = timeZone;
    this.originalStartDate = startDate.date ?? undefined;
    this.originalEndDate = endDate.date ?? undefined;
    reaction(
      () => this.startDate.date,
      () => {
        //End date follows start date plus template duration time until end date is set manually.
        if (
          this.startDate.date !== null &&
          (!this.endDate.isDirty ||
            (this.endDate.isDirty && !this.endDate.date))
        ) {
          this.endDate.date = new Date(
            this.startDate.date.getTime() + this.templateDurationTime
          );
        }

        const startDateOne = this.startDate.date;
        if (startDateOne) {
          if (isToday(startDateOne)) {
            const newHours = addHours(
              utcToZonedTime(new Date(), this.timeZone),
              1
            );
            this.startDate.setMinDate(newHours);
          }
          this.endDate.setMinDate(
            setMinutes(
              utcToZonedTime(startDateOne, this.timeZone),
              startDateOne.getMinutes() + 15
            )
          );
        }
      }
    );

    reaction(
      () => this.endDate.date,
      () => {
        const endDateOne = this.endDate.date;
        const startDateOne = this.startDate.date;
        if (endDateOne && startDateOne) {
          this.endDate.setMinDate(
            setMinutes(
              utcToZonedTime(startDateOne, this.timeZone),
              startDateOne.getMinutes() + 15
            )
          );
        }
      }
    );
  }

  //get duration time in milliseconds
  getDurationTime(
    templateDurationHour?: number,
    templateDurationMin?: number
  ): number {
    let totalMinutes =
      (templateDurationMin ?? 0) + (templateDurationHour ?? 0) * 60;
    return totalMinutes * 60000 || 3600000;
  }

  setIsDirty = (value: boolean) => {
    this.startDate.isDirty = value;
    this.endDate.isDirty = value;
  };

  checkIsDirty() {
    return !(this.startDate.isDirty || this.endDate.isDirty);
  }

  hasDateChange(): boolean {
    return this.startDate.hasChange || this.endDate.hasChange;
  }

  get isValid(): boolean {
    return (
      !this.useValidation ||
      !this.checkIsDirty() ||
      (this.isStartDateValid &&
        this.isEndDateValid &&
        this.isEndDateAfterStartDate)
    );
  }

  // if we need to validate a range of time again, make a timeRangeObservable
  get isEndDateAfterStartDate(): boolean {
    return (
      !this.useValidation ||
      !this.endDate.isDirty ||
      (this.startDate.date !== null &&
        this.endDate.date !== null &&
        this.startDate.date.getTime() < this.endDate.date.getTime())
    );
  }

  get isStartDateValid(): boolean {
    return (
      !this.useValidation ||
      !this.startDate.isDirty ||
      (this.startDate.date !== null &&
        utcToZonedTime(this.startDate.date, this.timeZone).getTime() >=
          utcToZonedTime(new Date(), this.timeZone).getTime())
    );
  }

  get isStartDateOneHourInFuture(): boolean {
    return (
      this.startDate.date !== null &&
      utcToZonedTime(this.startDate.date, this.timeZone).getTime() >=
        addHours(utcToZonedTime(new Date(), this.timeZone), 1).getTime()
    );
  }

  get isEndDateValid(): boolean {
    return (
      !this.useValidation ||
      !this.endDate.isDirty ||
      (this.endDate.date !== null &&
        utcToZonedTime(this.endDate.date, this.timeZone).getTime() >=
          utcToZonedTime(new Date(), this.timeZone).getTime())
    );
  }

  isEventInPast(): boolean {
    return this.endDate.date! < utcToZonedTime(new Date(), this.timeZone);
  }

  isEventTimeChanged() {
    return (
      this.originalStartDate?.getTime() !== this.startDate.date?.getTime() ||
      this.originalEndDate?.getTime() !== this.endDate.date?.getTime()
    );
  }
}
